import React, { useRef, useEffect, useId, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import * as Styled from '#/shared/components/Dialog/styled';

interface DialogProps {
  /** Title for this dialog; displayed as the heading */
  title: string;
  /** Text displayed on close button */
  closeButtonText: string;
  /** Method fired when close button or escape is pressed */
  closeDialog: () => void;
  /** The primary content to be displayed in the dialog */
  children: React.ReactNode;
  /** A ref for the button which opens the dialog */
  openingButtonRef: RefObject<HTMLButtonElement>;
}
/**
 * Focus is trapped inside the dialog by placing the inert attribute on other page content
 * This also ensures that screen readers can't accidentally move under the dialog
 * See: PageContainer in PageLayout/index
 */
function Dialog({
  title,
  closeButtonText,
  closeDialog,
  children,
  openingButtonRef,
}: DialogProps): React.ReactElement {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const intl = useIntl();

  const handleEscape = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeDialog();
    }
  };

  // Even though we've set inert on the rest of the page,
  // we have to manually place focus inside the dialog to ensure NVDA will read it
  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, []);

  // We can't return focus to the opening button until the page isn't inert anymore
  // So rather than including this in the close function, return focus there on unmount
  useEffect(() => {
    const buttonRefNode = openingButtonRef.current;
    return () => buttonRefNode?.focus();
  }, [openingButtonRef]);

  return createPortal(
    <>
      <Styled.DialogBackground />
      <Styled.DialogDiv
        role="dialog"
        aria-modal
        aria-labelledby={headingId}
        onKeyDown={handleEscape}
      >
        <Styled.TopBar>
          <Styled.TitleContainer>
            <Styled.Title id={headingId} tabIndex={-1} ref={headingRef}>
              {title}
            </Styled.Title>
          </Styled.TitleContainer>
          <Styled.CloseButton onClick={closeDialog}>
            <Styled.CloseButtonText>{closeButtonText}</Styled.CloseButtonText>
            <Styled.CloseIcon
              title={intl.formatMessage({ id: 'navbar.close-overlay' })}
            />
          </Styled.CloseButton>
        </Styled.TopBar>
        <Styled.ContentContainer>{children}</Styled.ContentContainer>
      </Styled.DialogDiv>
    </>,
    document.body
  );
}

export default Dialog;
